import { OnePieceCard } from '../types/onepiece';
import { transformApiCardToOnePieceCard } from '../utils/onepieceData';
import { tcgDataService } from './tcgDataService';

class OnePieceCardSearchService {
  private initialized = false;

  /**
   * Initialize the search service (no-op since moving to API)
   */
  async initialize(): Promise<void> {
    this.initialized = true;
  }

  /**
   * Search cards by name or number
   */
  async searchCards(query: string, limit: number = 20, offset: number = 0): Promise<{ cards: OnePieceCard[], total: number, hasMore: boolean }> {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/public/search?game=onepiece&q=${encodeURIComponent(query)}&limit=${limit}&offset=${offset}`);
        const data = await response.json();
        return {
            cards: data.cards.map((c: any) => transformApiCardToOnePieceCard(c)),
            total: data.total,
            hasMore: data.hasMore
        };
    } catch (error) {
        console.error('One Piece backend search failed:', error);
        return { cards: [], total: 0, hasMore: false };
    }
  }

  /**
   * Get cards by set ID
   */
  async getCardsBySet(setId: string): Promise<OnePieceCard[]> {
    return tcgDataService.getOnePieceSetCards(setId);
  }

  /**
   * Get a specific card by ID
   */
  async getCardById(cardId: string): Promise<OnePieceCard | null> {
    try {
        // Since we don't have a direct single card endpoint buy ID, we use search
        const results = await this.searchCards(cardId, 1);
        return results.cards.find(card => card.cardId === cardId) || null;
    } catch (error) {
        console.error(`Error fetching card ${cardId}:`, error);
        return null;
    }
  }

  /**
   * Filter cards with multiple criteria
   */
    async filterCardsAdvanced(filters: {
      searchTerm?: string;
      colors?: string[];
      rarities?: string[];
      categories?: string[];
      chaseOnly?: boolean;
      limit?: number;
      offset?: number;
    }): Promise<{ cards: OnePieceCard[], total: number, hasMore: boolean }> {
      try {
          const { searchTerm = '', colors = [], rarities = [], categories = [], chaseOnly = false, limit = 20, offset = 0 } = filters;
          const queryParams = new URLSearchParams({
              game: 'onepiece',
              q: searchTerm,
              limit: limit.toString(),
              offset: offset.toString(),
              colors: colors ? colors.join(',') : '',
              rarities: rarities ? rarities.join(',') : '',
              categories: categories ? categories.join(',') : '',
              chaseOnly: chaseOnly.toString()
          });
        
        const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/public/search?${queryParams.toString()}`);
        const data = await response.json();
        return {
            cards: data.cards.map((c: any) => transformApiCardToOnePieceCard(c)),
            total: data.total,
            hasMore: data.hasMore
        };
    } catch (error) {
        console.error('One Piece backend advanced search failed:', error);
        return { cards: [], total: 0, hasMore: false };
    }
  }

  /**
   * Get all available colors
   */
  getAvailableColors(): string[] {
    return ['Red', 'Blue', 'Green', 'Yellow', 'Purple', 'Black'];
  }

  /**
   * Get all available categories
   */
  getAvailableCategories(): string[] {
    return ['Leader', 'Character', 'Event', 'Stage'];
  }

  /**
   * Get all available rarities
   */
  getAvailableRarities(): string[] {
    return ['L', 'SEC', 'SR', 'R', 'UC', 'C', 'P'];
  }
}

// Export singleton instance
export const opCardSearchService = new OnePieceCardSearchService();
